import React, { useState } from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Icon,
  Typography,
  Switch,
  Tooltip,
  IconButton
} from '@material-ui/core';
import { useStyles } from './styles';
import { CommunicationType, LabelPrintCommand, LogLineResult, PrintCommand } from '../../../../models/labels';
import { HubConnection } from '@microsoft/signalr';
import { toI18nParams, notifications, extractApiErrorMessage } from '../../../../utils';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../../../hooks';
import {
  HealthSystemDALModelVetPrinterConfig,
  HealthSystemDomainVetsCommandsUpdateVeterinaryCommand,
  HealthSystemDomainVetsResultVeterinaryViewModel
} from '../../../../api/HealthSystemApi';

interface IProps {
  opened: boolean;
  setDialogOpen: (open: boolean) => void;
  isConnected: boolean;
  connection: HubConnection;
  veterinary: HealthSystemDomainVetsResultVeterinaryViewModel;
  start: () => void;
  stop: () => void;
  onConfigSaved: (config: HealthSystemDALModelVetPrinterConfig) => void;
}

const PrintCommandConfigModal: React.FC<any> = (props: IProps) => {
  const { opened, setDialogOpen, isConnected, connection, start, stop, veterinary, onConfigSaved } = props;

  const classes = useStyles();
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);

  const [commType, setCommType] = useState<CommunicationType>(CommunicationType.Driver);
  const [ip, setIp] = useState<string>('');
  const [port, setPort] = useState<number>(9100);
  const [usbPort, setUsbPort] = useState<string>('');
  const [driverName, setDriverName] = useState<string>('');

  React.useEffect(() => {
    if (opened) {
      const data = veterinary.settings.printerConfig;
      setCommType(data.communicationType);
      setIp(data.tcpIp?.ip ?? '');
      setPort(data.tcpIp?.port ?? 9100);
      setUsbPort(data.usb?.port ?? '/');
      setDriverName(data.driver?.name ?? '');
    }
  }, [opened, veterinary]);

  React.useEffect(() => {
    if (connection) {
      const handler = (result: LogLineResult[]) => {
        result.forEach((item) => {
          let message = t(item.messageId, toI18nParams(item.parameters));

          if (message === undefined) {
            message = item.messageId;
          }

          if (item.isSuccess) {
            notifications.success(message);
          } else {
            notifications.warning(message);
          }
        });
      };

      connection.on('check-print-command', handler);
    }
  }, [connection]);

  const closeDialog = (text: string | null) => {
    if (text == null) {
      setDialogOpen(false);
      return;
    }
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const printerConfig = buildPrinterConfig(commType, ip, port, usbPort, driverName);
    const command = buildUpdateCommand();
    command.settings.printerConfig = printerConfig;

    apiService.api.veterinary
      .veterinaryUpdateVeterinary(command)
      .then(() => {
        notifications.success(t('f-label-print-config-save-ok'));
        onConfigSaved(printerConfig);
        setIsBusy(false);
        setDialogOpen(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setIsBusy(false);
      });
  };

  const buildUpdateCommand = (): HealthSystemDomainVetsCommandsUpdateVeterinaryCommand => {
    // Cast because the `{}` fallback lacks the required `printerConfig`; handleSave sets it right after.
    return {
      id: veterinary?.id ?? '',
      name: veterinary?.name ?? '',
      number: veterinary?.number ?? '',
      settings: veterinary?.settings ?? {}
    } as HealthSystemDomainVetsCommandsUpdateVeterinaryCommand;
  };

  const getDefaultPrintConfig = (): HealthSystemDALModelVetPrinterConfig | null => {
    return {
      communicationType: CommunicationType.Driver,
      tcpIp: { ip: '', port: 9100 },
      usb: { port: '/' },
      driver: { name: 'SATO CT4-LX 305dpi' }
    };
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    const configCommand = buildPrinterConfig(commType, ip, port, usbPort, driverName);

    connection
      .invoke('check', JSON.stringify(configCommand))
      .then(() => {
        setIsBusy(false);
      })
      .catch((e: unknown) => {
        setIsBusy(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const handlePrintTestLabel = () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    const labelPrintCommand = {
      id: '0x00000000000001',
      medicalSupply: 'Umbrella',
      animalType: 'Piglet (NE)',
      method: 'Oral',
      size: '342',
      dose: '9',
      minVolumeCC: '00009',
      maxVolumeCC: '99999',
      startVolumeCC: '0',
      incrementStep: '0.099',
      withdrawal: '09090',
      withdrawalFor: '01010',
      orderNumber: 'ZK282',
      treatNumber: '11113',
      lotNumber: '#999',
      vetName: 'Vash Stampede',
      vetContactName: 'Edward Elric',
      regNumber: '115',
      vetAddress: 'Stefan cel mare 43',
      vetPhone: '33333333333',
      farmNumber: '117',
      farmName: 'Mother Base',
      farmAddress: 'Baker Str.',
      date: '01/01/0001',
      daysToUse: '99'
    } as LabelPrintCommand;

    const configCommand = buildPrinterConfig(commType, ip, port, usbPort, driverName);
    const payload: PrintCommand = { payload: labelPrintCommand, config: configCommand };

    connection
      .invoke('PrintCommand', JSON.stringify(payload))
      .then(() => {
        setIsBusy(false);
      })
      .catch((e: unknown) => {
        setIsBusy(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const validateForm = (): boolean => {
    switch (commType) {
      case CommunicationType.TcpIp:
        if (!ip) {
          notifications.warning(t('f-val-ip'));
          return false;
        }

        if (!port) {
          notifications.warning(t('f-val-port'));
          return false;
        }
        break;

      case CommunicationType.Usb:
        if (!usbPort) {
          notifications.warning(t('f-val-usb-port'));
          return false;
        }
        break;

      case CommunicationType.Driver:
        if (!driverName) {
          notifications.warning(t('f-val-driver-name'));
          return false;
        }
        break;
    }

    return true;
  };

  const buildPrinterConfig = (
    commType: CommunicationType,
    ip?: string,
    port?: number,
    usbPort?: string,
    driverName?: string
  ): HealthSystemDALModelVetPrinterConfig => {
    // Cast because `usbPort` is optional here but required by the DTO; every caller passes a string.
    return {
      communicationType: commType,
      tcpIp: { ip: ip ?? '', port: Number(port ?? 0) },
      usb: { port: usbPort },
      driver: { name: driverName ?? '' }
    } as HealthSystemDALModelVetPrinterConfig;
  };

  const toggleConnection = () => {
    if (isConnected) {
      stop();
    } else {
      start();
    }
  };

  return (
    <Dialog
      id="printCommandConfigDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      className={classes.dialog}
      maxWidth="xs"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="printCommandConfigDialogTitle" className={classes.dialogHeaderFooter}>
        {t('f-label-print-config-title')}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="right" justifyContent="space-between">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>{isConnected ? t('f-conn-print-adapter-on') : t('f-conn-print-adapter-off')}</Typography>

                <Tooltip arrow title={isConnected ? t('f-com-disable') : t('f-com-enable')}>
                  <Switch
                    edge="end"
                    color="primary"
                    onChange={toggleConnection}
                    checked={isConnected}
                    disabled={isBusy}
                  />
                </Tooltip>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Communication Type"
                value={commType}
                fullWidth
                variant="outlined"
                size="small"
                onChange={(e) => setCommType(e.target.value as unknown as CommunicationType)}
                disabled={!isConnected || isBusy}
              >
                <MenuItem value={CommunicationType.TcpIp}>TCP/IP</MenuItem>
                <MenuItem value={CommunicationType.Usb}>USB</MenuItem>
                <MenuItem value={CommunicationType.Driver}>Driver</MenuItem>
              </TextField>
            </Grid>

            {commType === CommunicationType.TcpIp && (
              <>
                <Grid item xs={8}>
                  <TextField
                    label={t('f-ip-address')}
                    value={ip}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={(e) => setIp(e.target.value)}
                    disabled={!isConnected || isBusy}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={t('f-port')}
                    type="number"
                    value={port}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v >= 0 && v <= 65535) setPort(v);
                    }}
                    disabled={!isConnected || isBusy}
                  />
                </Grid>
              </>
            )}

            {commType === CommunicationType.Usb && <></>}

            {commType === CommunicationType.Driver && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <TextField
                    label={t('f-driver-name')}
                    value={driverName}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={(e) => setDriverName(e.target.value)}
                    disabled={!isConnected || isBusy}
                  />
                  <Tooltip arrow title={t('f-driver-name-reset-to-default')}>
                    <IconButton
                      onClick={() => {
                        const data = getDefaultPrintConfig();
                        if (data) {
                          setDriverName(data.driver?.name ?? '');
                          setPort(data.tcpIp?.port ?? 9100);
                          setIp(data.tcpIp?.ip ?? '');
                          setUsbPort(data.usb?.port ?? '');
                          setCommType(data.communicationType);
                        }
                      }}
                      size="small"
                    >
                      <Icon>undo</Icon>
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                variant="contained"
                size="small"
                startIcon={<Icon>{'save'}</Icon>}
                color="primary"
                id="save-btn"
                onClick={() => {
                  handleSave();
                  closeDialog(null);
                }}
                disabled={!isConnected || isBusy}
              >
                {t('f-label-save-btn')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        {isBusy && <CircularProgress size={24} />}

        <Button
          variant="outlined"
          size="small"
          startIcon={<Icon>{'add_link'}</Icon>}
          color="secondary"
          id="check-btn"
          onClick={() => handleSubmit()}
          disabled={!isConnected || isBusy}
        >
          {t('f-label-print-btn')}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<Icon>{'print'}</Icon>}
          color="primary"
          id="test-btn"
          onClick={() => handlePrintTestLabel()}
          disabled={!isConnected || isBusy}
        >
          {t('f-label-print-test-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintCommandConfigModal;
